<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'Scaling GMP AI Through Existing Quality Systems — QikSolve',
  description:
    'How GMP teams can test and scale AI governance by extending existing quality-system discipline, ownership, evidence, review, and continual improvement.',
});

const page = {
  hero: {
    eyebrow: 'Scaling GMP AI through quality systems',
    title: 'Scale AI by extending quality practice, not creating a parallel system',
    summary:
      'GMP teams already understand intended use, ownership, evidence, review, change control, and continual improvement. Those disciplines provide a practical foundation for AI governance and governed AI.',
    cta: { label: 'Discuss your GMP AI pathway', to: '/contact' },
  },
  intro: {
    title: 'The quality system is the starting point',
    content:
      'The opportunity is not to invent a separate governance stack. It is to connect AI-assisted work to the quality objectives, process ownership, evidence, and review practices the organisation already knows how to operate.',
    items: [
      { title: 'Start with one meaningful use case', description: 'Choose a defined quality or operational problem with a clear owner and practical next step.' },
      { title: 'Map the operating model', description: 'Connect people, process, information, technology, decisions, review, and feedback.' },
      { title: 'Improve through evidence', description: 'Use findings, effectiveness checks, and controlled change to mature the workflow over time.' },
    ],
  },
  sections: [
    {
      title: 'What makes scaling trustworthy',
      items: [
        { title: 'Quality objectives stay visible', description: 'AI activity remains connected to the quality or operational outcome it is meant to support.' },
        { title: 'Ownership scales with use', description: 'Accountable roles, review authority, escalation, and evidence do not disappear as volume grows.' },
        { title: 'Control remains proportionate', description: 'The governance response follows process impact instead of applying the same burden to every use case.' },
        { title: 'Learning is deliberate', description: 'The organisation uses review results, exceptions, and change evidence to improve safely.' },
      ],
    },
    {
      title: 'From pilot to practical capability',
      items: [
        { title: 'Define the boundary', description: 'Clarify what the AI-assisted workflow can and cannot do.' },
        { title: 'Run a governed evaluation', description: 'Use agreed criteria, review, evidence, and escalation before expanding use.' },
        { title: 'Connect to the next path', description: 'Move to service support or QxAIOS solution depth when the use case and fit are clear.' },
      ],
    },
  ],
  callout: {
    title: 'Trust is built through observable practice',
    content: 'QikSolve helps teams connect AI adoption to the quality system they already operate. QxAIOS is the solution destination for platform and product-depth questions.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Continue the decision',
    items: [
      { label: 'Practical, governed AI hub', description: 'Return to the full Pillar 5 governance pathway.', to: '/product/agentic-ai-governance' },
      { label: 'Choose an AI use case', description: 'Start with a defined GMP quality or operational problem.', to: '/product/choosing-ai-use-cases-for-gmp-quality-work' },
      { label: 'Explore QxAIOS', description: 'Visit the QxAIOS solution for platform and product depth.', to: 'https://qx.qiksolve.com' },
    ],
  },
  cta: { title: 'Ready to scale a defined GMP AI use case?', content: 'Bring the quality objective, operating context, and evidence questions your team is working through.', label: 'Discuss your pathway', to: '/contact' },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>
